import { computed, reactive, ref, watch, type Ref } from "vue";
import { useGetCatalogues } from "@/stores/queries/catalogues.query";
import CatalogueSessionState from "@/stores/report/CatalogueSessionState";
import {
  CUSTOM_SYSTEM_CODE,
  findSystemForGrade,
  getNotationSystem,
  getSystemByCode,
  isCustomSystem,
  type NotationSystem,
  type NotationSystemCode,
} from "@/constants/notationSystems";

const REPORT_KEY_PREFIX = "inspexio.notationSystem.report.";
const LAST_USED_KEY = "inspexio.notationSystem.lastUsed";

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    // Mode privé ou stockage saturé : on retombe sur les valeurs déduites.
    return null;
  }
};

const writeStorage = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Sans persistance, le choix reste valable pour la session en cours.
  }
};

// État partagé par tous les appelants du composable, au niveau du module.
// Sans cela, chaque composant garderait sa propre copie : le formulaire pourrait
// changer de système sans que le tableau des constatations en soit informé.
const codeByStorageKey = reactive<Record<string, string>>({});
const lastUsedCode = ref<string | null>(readStorage(LAST_USED_KEY));

const hydrateFromStorage = (key: string | null): void => {
  if (!key || key in codeByStorageKey) return;
  const stored = readStorage(key);
  if (stored) codeByStorageKey[key] = stored;
};

/**
 * Système de notation retenu pour un rapport donné.
 *
 * Le choix est mémorisé par rapport, pour que toutes les saisies de notes — le
 * formulaire de constatation comme la cellule du tableau — parlent le même
 * langage. Il est aussi mémorisé globalement : le dernier système utilisé
 * devient la proposition par défaut du rapport suivant.
 *
 * Ordre de résolution quand le rapport n'a pas encore de choix enregistré :
 *   1. le dernier système choisi par l'inspecteur, tous rapports confondus ;
 *   2. le catalogue qu'il consulte dans l'onglet Constatations ;
 *   3. IQOA, seul système existant avant l'ajout de PM2I.
 */
export function useReportNotationSystem(reportId: Ref<number | undefined>) {
  const { data: catalogues } = useGetCatalogues();

  const storageKey = computed(() =>
    reportId.value ? `${REPORT_KEY_PREFIX}${reportId.value}` : null
  );

  hydrateFromStorage(storageKey.value);
  watch(storageKey, hydrateFromStorage);

  /** Système déduit du catalogue ouvert, utilisé tant que rien n'a été choisi. */
  const catalogueSystem = computed<NotationSystem>(() => {
    const catalogueId = CatalogueSessionState.lastCatalogueId;
    const catalogue = catalogueId
      ? catalogues.value?.find((c) => c.id === catalogueId)
      : undefined;
    return getNotationSystem(catalogue?.type);
  });

  const systemCode = computed<NotationSystemCode>(() => {
    const key = storageKey.value;
    return (
      (key ? codeByStorageKey[key] : undefined) ??
      lastUsedCode.value ??
      catalogueSystem.value.code
    );
  });

  const isCustom = computed(() => isCustomSystem(systemCode.value));

  /** Système courant, ou undefined en notation libre (elle n'a pas de grades). */
  const system = computed<NotationSystem | undefined>(() =>
    getSystemByCode(systemCode.value)
  );

  /** Notes proposées à la saisie ; vide en notation libre. */
  const gradeOptions = computed<string[]>(() =>
    (system.value?.grades ?? [])
      .filter((g) => g.level !== "none")
      .map((g) => g.value)
  );

  const setSystemCode = (code: NotationSystemCode): void => {
    const key = storageKey.value;
    if (key) {
      codeByStorageKey[key] = code;
      writeStorage(key, code);
    }
    lastUsedCode.value = code;
    writeStorage(LAST_USED_KEY, code);
  };

  /**
   * Système auquel appartient une note déjà saisie. Sert à convertir la valeur
   * quand l'inspecteur change de système.
   */
  const systemCodeOfValue = (value?: string | null): NotationSystemCode =>
    findSystemForGrade(value)?.code ?? CUSTOM_SYSTEM_CODE;

  return {
    systemCode,
    system,
    isCustom,
    gradeOptions,
    setSystemCode,
    systemCodeOfValue,
  };
}
